import React from 'react';
import { Badge } from '../../ui/badge';
import { AlertTriangle, XCircle } from 'lucide-react';
import { Stat } from '../canFrameDecoder/RawFrameCard';

const FIELD_TONES = {
  priority: 'bg-violet-100 border-violet-400 text-violet-900 dark:bg-violet-950/50 dark:text-violet-100',
  edp: 'bg-slate-100 border-slate-400 text-slate-900 dark:bg-slate-800 dark:text-slate-100',
  dp: 'bg-slate-100 border-slate-400 text-slate-900 dark:bg-slate-800 dark:text-slate-100',
  pf: 'bg-blue-100 border-blue-400 text-blue-900 dark:bg-blue-950/50 dark:text-blue-100',
  ps: 'bg-cyan-100 border-cyan-400 text-cyan-900 dark:bg-cyan-950/50 dark:text-cyan-100',
  sa: 'bg-emerald-100 border-emerald-400 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100',
};

// The 29 identifier bits grouped by field, then the derived PGN / PDU facts. All values come
// from the backend's breakdown.
const IdBreakdown = ({ breakdown, testId }) => {
  if (!breakdown) {
    return <p className="text-sm text-muted-foreground">Enter a valid 29-bit CAN ID to see its J1939 fields.</p>;
  }
  const b = breakdown;
  const psLabel = b.pdu === 1 ? 'PS = Destination Address' : 'PS = Group Extension';
  return (
    <div className="space-y-4" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-fields`}>
        {b.fields.map((f) => (
          <div key={f.key} className={`rounded-md border px-2 py-1.5 min-w-0 ${FIELD_TONES[f.key]}`} data-testid={`${testId}-field-${f.key}`}>
            <div className="flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-wider">
              <span className="font-bold">{f.label}</span>
              <span className="opacity-70">{f.fromBit === f.toBit ? `bit ${f.fromBit}` : `${f.fromBit}–${f.toBit}`}</span>
            </div>
            <div className="font-mono text-sm tracking-[0.15em] break-all">{f.bits}</div>
            <div className="font-mono text-xs">{f.hex} = {f.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat big label="PGN" value={b.pgn} sub={b.pgnHex} testId={`${testId}-pgn`} />
        <Stat label="PDU Format" value={`PDU${b.pdu}`} sub={b.pdu === 1 ? `PF ${b.pf} < 240: peer-to-peer` : `PF ${b.pf} ≥ 240: broadcast`} testId={`${testId}-pdu`} />
        {b.pdu === 1 ? (
          <Stat
            label="Destination Address"
            value={b.destination.hex}
            sub={b.destination.broadcast ? 'Global (all nodes)' : `node ${b.destination.address}; not part of the PGN`}
            testId={`${testId}-da`}
          />
        ) : (
          <Stat label="Group Extension" value={b.groupExtension.hex} sub="part of the PGN" testId={`${testId}-ge`} />
        )}
        <Stat label="Priority / Source Address" value={`${b.priority} / ${b.saHex}`} sub={`SA ${b.sa}`} testId={`${testId}-sa`} />
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <Badge variant="outline" className="font-mono">{psLabel}</Badge>
        {b.pdu === 1 && b.destination.broadcast && <Badge variant="outline" className="font-mono" data-testid={`${testId}-broadcast`}>Broadcast (DA 0xFF)</Badge>}
        <span className="font-mono">DBC BO_ ID = {b.canIdHex} | 0x80000000 = {b.dbcId}</span>
      </div>

      {b.notes.length > 0 && (
        <ul className="space-y-1" data-testid={`${testId}-notes`}>
          {b.notes.map((n) => (
            <li key={n.message} className={`flex items-start gap-1.5 text-xs ${n.severity === 'error' ? 'text-destructive' : 'text-amber-700 dark:text-amber-400'}`}>
              {n.severity === 'error' ? <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
              {n.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default IdBreakdown;
